<script setup lang="ts">
/**
 * Druckfreigabe vor dem Warenkorb: Proof-Bild (Server-Render mit Schnittlinie, sonst Client-Snapshot),
 * Warnliste, Farbhinweis, Pflicht-Checkbox.
 */
import { computed, ref } from 'vue'
import type { ProductGeometry } from '@pfe/design-renderer'

const props = defineProps<{
  status: 'loading' | 'ready' | 'fallback' | 'error'
  imageUrl: string
  warnings: Array<{ code?: string; message?: string; layerId?: string; severity?: string }>
  error?: string
  colorHint?: string
  geometry: ProductGeometry | null
}>()
const emit = defineEmits<{ (e: 'confirm'): void; (e: 'cancel'): void }>()
const accepted = ref(false)
const canConfirm = computed(() => accepted.value && (props.status === 'ready' || props.status === 'fallback'))
const shapeLabel = computed(() => ({ rect: 'rechteckig', rounded: 'mit abgerundeten Ecken', circle: 'rund', hexagon: 'sechseckig', custom: 'in Sonderform' } as Record<string, string>)[props.geometry?.shape || 'rect'] || '')
const warningText = (w: { code?: string; message?: string }) => w.message || ({
  LOW_DPI: 'Ein Bild hat eine niedrige Auflösung und kann unscharf gedruckt werden.',
  CONTENT_IN_BLEED: 'Ein Element liegt im Beschnitt und wird teilweise abgeschnitten.',
  TEXT_OUTSIDE_SAFE: 'Text liegt nahe der Schnittkante.',
  EMPTY_SLOT: 'Ein Bildplatz ist leer.',
  TEXT_OVERFLOW: 'Ein Text passt nicht vollständig in sein Feld.',
  FONT_MISSING: 'Eine Schriftart fehlt.',
  IMAGE_NOT_READY: 'Ein Bild ist noch nicht fertig hochgeladen.',
  IMAGE_NOT_COVERING: 'Ein Bild füllt seinen Rahmen nicht vollständig.',
} as Record<string, string>)[w.code || ''] || w.code || 'Hinweis'
</script>

<template>
  <div class="proof" role="dialog" aria-modal="true" aria-labelledby="proof-title" @keydown.esc="emit('cancel')">
    <div class="proof__card">
      <header class="proof__head">
        <strong id="proof-title">Druckfreigabe</strong>
        <button type="button" class="proof__x" aria-label="Schließen" @click="emit('cancel')">✕</button>
      </header>

      <div class="proof__body">
        <div class="proof__img">
          <p v-if="status === 'loading'" class="proof__state">Druckvorschau wird erzeugt…</p>
          <p v-else-if="status === 'error'" class="proof__state proof__state--err">Die Druckvorschau konnte nicht erzeugt werden.<br /><small>{{ error }}</small></p>
          <img v-else :src="imageUrl" alt="Druckvorschau" />
          <p v-if="status === 'fallback'" class="proof__note">Bildschirm-Vorschau (Server-Vorschau nicht verfügbar). Gestrichelt = Schnittkante.</p>
        </div>
        <div class="proof__info">
          <p v-if="geometry">
            Endformat <strong>{{ geometry.trimWidthMm }} × {{ geometry.trimHeightMm }} mm</strong>{{ shapeLabel ? ', ' + shapeLabel : '' }}.
            <template v-if="geometry.bleedMm > 0">Außerhalb der gestrichelten Linie werden {{ geometry.bleedMm }} mm abgeschnitten.</template>
          </p>
          <ul v-if="warnings.length" class="proof__warnings">
            <li v-for="(w, i) in warnings" :key="i" :data-severity="w.severity || 'warn'">{{ warningText(w) }}</li>
          </ul>
          <p v-else class="proof__ok">Keine Hinweise – das Design ist druckfertig.</p>
          <p v-if="colorHint" class="proof__hint">{{ colorHint }}</p>
          <label class="proof__accept">
            <input v-model="accepted" type="checkbox" />
            <span>Ich habe die Druckvorschau geprüft. Rechtschreibung, Bildausschnitt und Beschnitt sind so gewünscht.</span>
          </label>
        </div>
      </div>

      <footer class="proof__foot">
        <button type="button" class="proof__btn" @click="emit('cancel')">Weiter bearbeiten</button>
        <button type="button" class="proof__btn proof__btn--primary" :disabled="!canConfirm" @click="emit('confirm')">Freigeben &amp; in den Warenkorb</button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.proof { position: fixed; inset: 0; z-index: 60; background: rgba(15, 23, 42, 0.55); display: flex; align-items: center; justify-content: center; padding: 12px; }
.proof__card { background: #fff; border-radius: 14px; width: min(980px, 100%); max-height: min(92vh, 92dvh); display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35); }
.proof__head, .proof__foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 16px; }
.proof__head { border-bottom: 1px solid rgba(15, 23, 42, 0.12); font-size: 16px; }
.proof__foot { border-top: 1px solid rgba(15, 23, 42, 0.12); justify-content: flex-end; }
.proof__x { border: 0; background: transparent; cursor: pointer; font-size: 16px; }
.proof__body { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; padding: 16px; overflow: auto; min-height: 0; }
.proof__img { background: repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 0 0 / 20px 20px; border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 240px; padding: 12px; }
.proof__img img { max-width: 100%; max-height: 60vh; box-shadow: 0 4px 24px rgba(15, 23, 42, 0.25); }
.proof__state { font-size: 14px; color: #475569; text-align: center; }
.proof__state--err { color: #b91c1c; }
.proof__note { font-size: 12px; color: #64748b; margin: 8px 0 0; text-align: center; }
.proof__info { font-size: 14px; display: flex; flex-direction: column; gap: 12px; }
.proof__warnings { margin: 0; padding: 0 0 0 18px; display: flex; flex-direction: column; gap: 6px; }
.proof__warnings li[data-severity='block'] { color: #b91c1c; font-weight: 600; }
.proof__ok { color: #047857; }
.proof__hint { font-size: 12px; color: #64748b; background: #f8fafc; padding: 8px 10px; border-radius: 8px; margin: 0; }
.proof__accept { display: flex; gap: 10px; align-items: flex-start; font-size: 13px; line-height: 1.4; }
.proof__accept input { margin-top: 3px; width: 18px; height: 18px; }
.proof__btn { height: 38px; padding: 0 16px; border-radius: 8px; border: 1px solid rgba(15, 23, 42, 0.2); background: #fff; font: inherit; cursor: pointer; }
.proof__btn--primary { background: var(--pfe-color-accent, #e30b17); color: #fff; border-color: transparent; font-weight: 600; }
.proof__btn:disabled { opacity: 0.5; cursor: default; }
@media (max-width: 640px) {
  .proof { padding: 0; align-items: flex-end; }
  .proof__card { border-radius: 14px 14px 0 0; max-height: 100dvh; }
  .proof__body { grid-template-columns: 1fr; }
  .proof__foot { flex-direction: column-reverse; align-items: stretch; }
}
</style>
